import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@iconify/icons-mdi/delete";
import TextBoxIcon from "@iconify/icons-mdi/text-box";
import { InlineIcon } from "@iconify/react";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcFormSwitch,
  OcPageTransition,
} from "oc/components";

interface DocumentProps {
  _id: string;
  description?: string;
  userView: number;
  filename: string;
}

interface MyDocumentsEditFileProps {
  document: DocumentProps;
  removeUpload: (document: DocumentProps) => void;
  updateUpload: (args: {
    variables: { _id: string; description: string; userView: boolean };
  }) => Promise<any>;
  onClose: () => void;
  isMobile: boolean;
  refetch?: () => Promise<any>;
}

export const MyDocumentsEditFile: React.FC<MyDocumentsEditFileProps> = ({
  document,
  removeUpload,
  updateUpload,
  onClose,
  isMobile,
  refetch
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [description, setDescription] = useState(document?.description || "");
  const [userView, setUserView] = useState<boolean>(
    Boolean(document?.userView),
  );

  const [prevDescription, setPrevDescription] = useState(
    document?.description || "",
  );
  const [prevUserView, setPrevUserView] = useState(Boolean(document?.userView));

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);

  const handleRemoveUpload = () => {
    setShowConfirmDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    removeUpload(document);
    onClose();
  };

  const handleSave = async () => {
    const updatedDescription =
      description !== prevDescription ? description : prevDescription;
    const updatedUserView =
      Boolean(userView) !== prevUserView
        ? Boolean(userView)
        : Boolean(prevUserView);
    await updateUpload({
      variables: {
        _id: document._id,
        description: updatedDescription,
        userView: updatedUserView,
      },
    });

    if (refetch) {
      await refetch();
    }

    setPrevDescription(updatedDescription);
    setPrevUserView(updatedUserView);
    onClose();
  };

  const handleCancel = () => {
    setDescription(prevDescription);
    setUserView(prevUserView);
    onClose();
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleUserViewChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserView(e.target.checked);
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={() => {
          handleSave();
        }}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcConfirmationDialog
        showDialog={showConfirmDeleteDialog}
        onClose={handleConfirmDelete}
        onCancel={() => setShowConfirmDeleteDialog(false)}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcPageTransition>
        <OcCard label={t("MY_DOCUMENTS_EDIT_DOCUMENT")}>
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems={isMobile ? "unset" : "center"}
          >
            <Box
              key={document?._id}
              display="flex"
              flexDirection="row"
              alignItems="center"
              style={{
                width: isMobile ? "auto" : "459px",
                backgroundColor: theme.palette.gray20.main,
                padding: "16px 20px",
                borderRadius: "8px",
                border: "1px solid #C6CACC",
              }}
            >
              <Box>
                <InlineIcon
                  icon={TextBoxIcon}
                  width="24px"
                  style={{ color: "#4d4d4d" }}
                />
              </Box>
              <Box sx={{ width: "100%", padding: "0px 16px" }}>
                <Typography variant="body1">{document?.filename}</Typography>
              </Box>
              <Box>
                <InlineIcon
                  id="delete-icon"
                  icon={DeleteIcon}
                  width="24px"
                  style={{
                    color: "#4d4d4d",
                    cursor: "pointer",
                  }}
                  onClick={handleRemoveUpload}
                />
              </Box>
              <Box height="32px" />
            </Box>
            <Box height="32px" />
            <TextField
              id="designation"
              value={description}
              variant="outlined"
              label={t("OC_DESIGNATION")}
              type="string"
              onChange={handleDescriptionChange}
              style={{
                width: isMobile ? "100%" : "500px",
              }}
            />
            <Box height="32px" />
          </Box>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box display="flex" alignItems="center" width="500px">
              <OcFormSwitch
                id="employeeVisibility"
                checked={userView}
                inputProps={{ "aria-label": "controlled" }}
                onChange={handleUserViewChange}
                disabled={false}
              />
              <Box width="8px" />
              <Typography variant="body1">
                {t("MY_DOCUMENTS_EMPLOYEE_VISIBILITY")}
              </Typography>
            </Box>
          </Box>
          <OcCardButtonBar
            handleRightButton={handleCancel}
            rightButtonColor="secondary"
            rightButtonText={t("OC_CANCEL")}
            handlePrimaryButton={() => setShowConfirmDialog(true)}
            primaryButtonText={t("OC_SAVE")}
            primaryButtonDisabled={
              description === prevDescription && userView === prevUserView
            }
          />
        </OcCard>
      </OcPageTransition>
    </>
  );
};

export default MyDocumentsEditFile;
